import { useEffect } from "react"
import { useMap } from "react-leaflet"
import L from "leaflet"

import usePortsGeojson from "@/hooks/usePortsGeojson"
import { formatLatLngDM } from "@/utils/geoFormat"
import { bindMapCard, useCloseMapCardWhenHidden } from "@/features/map/mapCardStore"
import { createGisCluster, declutterLabels, gisMarkerIcon } from "@/features/gis/gisCluster"
import { GIS_MARKERS } from "@/features/gis/gisMarkers"

const portIcon = gisMarkerIcon(GIS_MARKERS.port.Icon, GIS_MARKERS.port.color, "port-icon")
// Extra label lines, each shown by CSS from its zoom level on (class on the map container).
const DETAIL_LINES = [
  { key: "terminal", zoom: 13 },
  { key: "berth", zoom: 15 },
]

// Berths of one port sit within a few hundred metres: keep them grouped until the icons separate.
const PORT_CLUSTER_OFF_ZOOM = 16
const portClusterRadius = (zoom) => (zoom >= 11 ? 28 : Math.max(60 - (zoom - 6) * 6, 20))

// From terminal zoom on the terminal replaces the port name, which the group label already shows.
function portLabel(properties) {
  const el = document.createElement("div")
  if (properties.terminal) el.className = "has-terminal"
  el.dataset.priority = Number(properties.tonnage) || 0
  const name = document.createElement("div")
  name.className = "port-label-name"
  name.textContent = properties.name
  el.appendChild(name)
  DETAIL_LINES.forEach(({ key }) => {
    if (!properties[key]) return
    const line = document.createElement("div")
    line.className = `port-label-${key}`
    line.textContent = properties[key]
    el.appendChild(line)
  })
  return el
}

export default function Ports({ cwPortGeojson, setCwPortGeojson, toggleFlags, switchGisLayersActive }) {
  const portsEnabled = switchGisLayersActive && toggleFlags.includes("Ports")
  useCloseMapCardWhenHidden("port-", portsEnabled)
  const map = useMap()

  usePortsGeojson({
    enabled: portsEnabled,
    cwPortGeojson,
    setCwPortGeojson,
  })

  useEffect(() => {
    if (!portsEnabled || !cwPortGeojson?.features) return undefined
    const cluster = createGisCluster(map, {
      rgb: GIS_MARKERS.port.rgb,
      text: "white",
      clusterOffZoom: PORT_CLUSTER_OFF_ZOOM,
      maxClusterRadius: portClusterRadius,
      icon: GIS_MARKERS.port.Icon,
      label: (group) => {
        const names = new Set(group.getAllChildMarkers().map((m) => m.options.portName))
        return names.size === 1 ? [...names][0] : null
      },
    })
    const markers = cwPortGeojson.features
      .filter((feature) => Array.isArray(feature?.geometry?.coordinates))
      .map((feature) => {
        const p = feature.properties ?? {}
        const [lng, lat] = feature.geometry.coordinates
        const marker = L.marker([lat, lng], { icon: portIcon, portName: p.name })
        // Only a marker out of its cluster is on the map, so the label shows once the group resolves.
        if (p.name) {
          marker.bindTooltip(() => portLabel(p), {
            permanent: true,
            direction: "right",
            offset: L.point(16, 0),
            className: "port-label",
          })
        }
        // While the planner places points, CSS lets clicks through (GisInteractivityProvider).
        bindMapCard(
          marker,
          () => ({
            key: `port-${p.cw_id ?? `${p.name}-${lat}-${lng}`}`,
            title: p.name + (p.unlocode ? ` (${p.unlocode})` : ""),
            subtitle: "Port",
            rows: [
              ["Terminal", p.terminal],
              ["Berth", p.berth],
              ["ID", p.cw_id],
              ["Position", formatLatLngDM(lng, lat)],
            ],
          }),
          () => L.latLng(lat, lng),
        )
        return marker
      })
    cluster.addLayers(markers)
    cluster.addTo(map)
    const container = map.getContainer()
    const toggleDetails = () =>
      DETAIL_LINES.forEach(({ key, zoom }) =>
        container.classList.toggle(`ports-show-${key}`, map.getZoom() >= zoom),
      )
    toggleDetails()
    map.on("zoomend", toggleDetails)
    const stopDeclutter = declutterLabels(map, {
      labelSelector: ".leaflet-tooltip.port-label",
      obstacleSelector: ".port-icon, .custom-cluster-icon, .gis-cluster-label",
      cluster,
    })
    return () => {
      stopDeclutter()
      map.off("zoomend", toggleDetails)
      DETAIL_LINES.forEach(({ key }) => container.classList.remove(`ports-show-${key}`))
      map.removeLayer(cluster)
    }
  }, [map, portsEnabled, cwPortGeojson])

  return null
}
